import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, FileText, Loader2, AlertCircle } from 'lucide-react';
import api from '@/lib/api';
import { formatDate } from '@/lib/utils';

/**
 * Build a fully-qualified URL for a partner-document file_url like
 * "/api/uploads/partner_documents/<partner_id>/<filename>". The URL stored
 * in MongoDB is host-relative; we prepend the configured backend host so
 * the link works both on the deployed app and from local dev tooling.
 */
function resolveFileUrl(fileUrl) {
  if (!fileUrl) return '#';
  if (fileUrl.startsWith('http')) return fileUrl;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return `${base}${fileUrl}`;
}

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function PartnerDocumentsDialog({ open, onOpenChange, partner }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [docs, setDocs] = useState([]);

  const load = useCallback(async () => {
    if (!partner?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/admin/partners/${partner.id}/documents`);
      setDocs(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load partner documents');
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [partner?.id]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="partner-documents-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-slate-600" />
            Documents — {partner?.business_name || 'Partner'}
          </DialogTitle>
          <DialogDescription>
            Files uploaded by this partner. Click a link to open the file in a new tab.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-[140px]" data-testid="partner-documents-body">
          {loading && (
            <div className="flex items-center justify-center py-10 text-slate-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading documents...
            </div>
          )}

          {!loading && error && (
            <div
              data-testid="partner-documents-error"
              className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-sm px-3 py-2"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && docs.length === 0 && (
            <div
              data-testid="partner-documents-empty"
              className="flex flex-col items-center justify-center py-8 text-center text-slate-500"
            >
              <FileText className="h-8 w-8 text-slate-300 mb-2" />
              <p className="text-sm">No documents uploaded yet.</p>
              <p className="text-xs text-slate-400 mt-1">
                Partner will upload ID, insurance, certificates, etc. from their Profile.
              </p>
            </div>
          )}

          {!loading && !error && docs.length > 0 && (
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {docs.map((doc) => (
                <li
                  key={doc.id}
                  data-testid={`partner-document-row-${doc.id}`}
                  className="flex items-center justify-between gap-3 px-3 py-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
                        {doc.document_type?.replace(/_/g, ' ') || 'document'}
                      </Badge>
                      <span className="text-sm font-medium text-slate-900 truncate">
                        {doc.original_filename || 'file'}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {formatDate(doc.uploaded_at)}
                      {doc.size_bytes ? ` · ${formatSize(doc.size_bytes)}` : ''}
                      {doc.content_type ? ` · ${doc.content_type}` : ''}
                    </div>
                  </div>
                  <a
                    href={resolveFileUrl(doc.file_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`partner-document-link-${doc.id}`}
                    className="shrink-0 inline-flex items-center gap-1 text-sm text-rose-600 hover:text-rose-700 hover:underline font-medium"
                  >
                    View <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="partner-documents-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
